import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from './api';
import { RichEditor } from '../components/RichEditor';

const PAGE_LABELS = { datenschutz: 'Datenschutz', impressum: 'Impressum' };

const inputCls = 'min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3';

export function PagesList() {
  const [pages, setPages] = useState(null);
  useEffect(() => { api('/admin/pages').then((d) => setPages(d.pages)).catch(() => setPages([])); }, []);
  return (
    <div data-testid="pages-list">
      <h1 className="font-serif text-3xl font-bold">Seiten</h1>
      <p className="mt-2 text-sm text-brand-brown-soft">Rechtliche und redaktionelle Seiten bearbeiten. Weitere Seiten folgen.</p>
      <ul className="mt-6 space-y-3">
        {(pages || Object.keys(PAGE_LABELS).map((key) => ({ key }))).map((p) => (
          <li key={p.key} className="flex flex-wrap items-center justify-between gap-3 border border-brand-line bg-brand-cream p-4" data-testid={`page-row-${p.key}`}>
            <div>
              <p className="font-serif text-xl font-bold">{PAGE_LABELS[p.key]}</p>
              <p className="text-sm text-brand-brown-soft">
                /{p.key} · {p.status === 'published' ? 'Veröffentlicht' : p.status === 'draft' ? 'Entwurf' : 'Standardinhalt aktiv'}
                {p.updated_at && ` · geändert ${new Date(p.updated_at).toLocaleDateString('de-CH')}`}
              </p>
            </div>
            <Link to={`/admin/seiten/${p.key}`} className="btn-secondary" data-testid={`edit-page-${p.key}`}>Bearbeiten</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PageEditor() {
  const { key } = useParams();
  const label = PAGE_LABELS[key];
  const [page, setPage] = useState({ title: label || '', content: '', seo_title: '', meta_description: '', status: 'published' });
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!label) return;
    api('/admin/pages').then((d) => {
      const found = d.pages.find((p) => p.key === key);
      if (found && found.title) setPage({ title: found.title, content: found.content || '', seo_title: found.seo_title || '', meta_description: found.meta_description || '', status: found.status || 'published' });
    }).catch(() => {});
  }, [key, label]);

  if (!label) return <p role="alert" className="text-red-800">Unbekannte Seite.</p>;

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api(`/admin/pages/${key}`, { method: 'PUT', body: JSON.stringify(page) });
      setMessage({ type: 'success', text: `${label} gespeichert. Die öffentliche Seite /${key} zeigt den neuen Inhalt.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="page-editor">
      <h1 className="font-serif text-3xl font-bold">{label} bearbeiten</h1>
      <p className="mt-2 text-sm text-brand-brown-soft">
        Öffentliche Seite: <Link to={`/${key}`} target="_blank" rel="noopener" className="link-inline">/{key}</Link>
      </p>

      {message && (
        <p role={message.type === 'error' ? 'alert' : 'status'} className={`mt-4 rounded-sm border p-4 text-sm font-semibold ${message.type === 'error' ? 'border-red-700 bg-red-50 text-red-800' : 'border-brand-olive bg-white text-brand-olive-dark'}`} data-testid="page-message">
          {message.text}
        </p>
      )}

      <div className="mt-6 space-y-5 border border-brand-line bg-brand-cream p-5">
        <div>
          <label htmlFor="page-title" className="mb-1 block text-sm font-semibold">Seitentitel</label>
          <input id="page-title" value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })} className={inputCls} data-testid="page-title" />
        </div>
        <RichEditor id="page-content" label="Inhalt" value={page.content} onChange={(v) => setPage({ ...page, content: v })} rows={14} hint="Absätze, Überschriften (H2/H3), Listen und Links werden unterstützt. HTML ist aus Sicherheitsgründen nicht erlaubt." />
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <label htmlFor="page-seo-title" className="mb-1 block text-sm font-semibold">SEO Title</label>
            <input id="page-seo-title" value={page.seo_title} onChange={(e) => setPage({ ...page, seo_title: e.target.value })} className={inputCls} data-testid="page-seo-title" />
          </div>
          <div>
            <label htmlFor="page-seo-desc" className="mb-1 block text-sm font-semibold">Meta Description</label>
            <input id="page-seo-desc" value={page.meta_description} onChange={(e) => setPage({ ...page, meta_description: e.target.value })} className={inputCls} data-testid="page-seo-desc" />
          </div>
        </div>
        <div>
          <label htmlFor="page-status" className="mb-1 block text-sm font-semibold">Veröffentlichungsstatus</label>
          <select id="page-status" value={page.status} onChange={(e) => setPage({ ...page, status: e.target.value })} className={`${inputCls} sm:w-56`} data-testid="page-status">
            <option value="published">Veröffentlicht</option>
            <option value="draft">Entwurf (Standardinhalt bleibt sichtbar)</option>
          </select>
        </div>
        <button type="button" onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="page-save-button">
          {saving ? 'Speichere …' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
